import type { BulkReprocessPreviewItemDto } from '@sara/api-client';
import { X } from 'lucide-react';
import { Badge, type BadgeProps } from '@sara/ui/components/badge';
import { Button } from '@sara/ui/components/button';
import { DataPanel } from '@sara/ui/components/data-panel';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sara/ui/components/table';

interface BulkReprocessPreviewTableProps {
  count: number;
  page: number;
  pageSize: number;
  items: BulkReprocessPreviewItemDto[];
  onPageChange: (page: number) => void;
  isFetching: boolean;
  /** When provided, each row gets a control to drop it from the run. */
  onRemove?: (sessionId: string) => void;
}

function statusVariant(status: string): BadgeProps['variant'] {
  if (status === 'Completed') return 'success';
  if (status === 'Completed, no results') return 'warning';
  if (status === 'Failed') return 'destructive';
  return 'neutral';
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function BulkReprocessPreviewTable({
  count,
  page,
  pageSize,
  items,
  onPageChange,
  isFetching,
  onRemove,
}: BulkReprocessPreviewTableProps) {
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const from = count === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(count, page * pageSize);

  return (
    <DataPanel>
      <DataPanel.Body className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Session ID</TableHead>
              <TableHead>External user ID</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Status</TableHead>
              {onRemove && (
                <TableHead className="w-10">
                  <span className="sr-only">Remove</span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((row) => (
              <TableRow key={row.sessionId}>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {row.sessionId.slice(0, 8)}
                </TableCell>
                <TableCell className="font-mono text-xs">{row.externalUserId ?? '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatDate(row.createdAt)}
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                </TableCell>
                {onRemove && (
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      aria-label={`Remove session ${row.sessionId.slice(0, 8)} from the run`}
                      disabled={isFetching}
                      onClick={() => onRemove(row.sessionId)}
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={1.8} />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataPanel.Body>
      <DataPanel.Footer className="flex items-center justify-between">
        <span className="tabular-nums text-muted-foreground">
          {count === 0 ? 'No matching sessions' : `Showing ${from}–${to} of ${count}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1 || isFetching}
            onClick={() => onPageChange(page - 1)}
          >
            Prev
          </Button>
          <span className="tabular-nums text-[12.5px] text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page >= totalPages || isFetching}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      </DataPanel.Footer>
    </DataPanel>
  );
}
